import { useDomainContext } from "@hooks/useDomainContext";
import { ReactElement, useEffect, useState, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";

import Section from "./section";
import { modifyCSSForElement } from "@page-builder/utils";
const Modal = dynamic(() => import("@components/shared/modal"), { ssr: false });
import CloseIcon from "@components/svg/closeIcon";
import { SectionRules } from "@page-builder/ts/types";
import { getPhoneNumber } from "@lib/sharedUtils";
import { useStickyElement } from "@hooks/useStickyElement";

const allowedPageToShowLegalNotice = [
    "[category]",
    "[generic-route]",
    "[keyword]",
];

export default function Page(): ReactElement {
    const {
        routeConfig,
        category,
        domain,
        locale,
        domainNameTranslation,
        visitDetails,
        headerOffer,
        referrer,
        userAgent,
    } = useDomainContext();

    const [, forceUpdate] = useState(0);
    const allowedPage = routeConfig?.replacedPortions?.find((page) =>
        allowedPageToShowLegalNotice.includes(page.key),
    );

    let phoneNumber: string | undefined;
    if (category && headerOffer?.length) {
        phoneNumber = getPhoneNumber(headerOffer[0]);
    } else {
        phoneNumber = "";
    }

    const [showModal, setShowPartnerPopup] = useState(false);
    const [showLegalNotice, setShowLegalNotice] = useState(false);

    // Resolve whether to render all sections immediately (page overrides domain).
    // Default false preserves the deferred, hero-first rendering behavior.
    const renderSectionsImmediately = Boolean(
        routeConfig?.config?.settings?.renderAllSectionsImmediately ??
            domain?.config?.renderAllSectionsImmediately ??
            false,
    );
    const [renderAllSections, setRenderAllSections] = useState(
        renderSectionsImmediately,
    );

    // Collect all component CSS once (memoized to prevent recalculation)
    // Must be called before early return to follow Rules of Hooks
    const allComponentCSS = useMemo(() => {
        const sections = routeConfig?.config?.sections || [];
        return sections
            .flatMap((section, sectionIndex) =>
                section.components
                    .map((component, componentIndex) => {
                        // Preserve original componentIndex to match rendering
                        if (!component.css) return null;
                        return modifyCSSForElement(
                            component.css,
                            `#${component.id}-${componentIndex}-${sectionIndex}`,
                        );
                    })
                    .filter(Boolean),
            )
            .join("\n");
    }, [routeConfig?.config?.sections]);

    useEffect(() => {
        if (!showModal) return;

        // Check if script already exists
        const existingScript = document.querySelector(
            'script[src="https://dwy9ix7d387oz.cloudfront.net/scripts/embed.js"]',
        );

        if (!existingScript) {
            const script = document.createElement("script");
            script.src =
                "https://dwy9ix7d387oz.cloudfront.net/scripts/embed.js";
            script.setAttribute("data-container", "participants-container");
            script.onload = () => {
                // Force re-render after script loads
                setTimeout(() => forceUpdate((n) => n + 1), 100);
            };
            document.body.appendChild(script);
            window.ParticipantsDB?.load("participants-container");
        } else {
            // Script already loaded, just force re-render
            window.ParticipantsDB?.load("participants-container");
            forceUpdate((n) => n + 1);
        }
    }, [showModal]);

    useEffect(() => {
        setShowLegalNotice(
            (category?.slug === "auto-accident" &&
                !domain.config?.hideLegalNotice &&
                (allowedPage ||
                    routeConfig?.config?.settings?.showLegalNotice ||
                    window.location.pathname === "/" ||
                    window.location.pathname === `/${locale}` ||
                    window.location.pathname.includes("free-consultation") ||
                    window.location.pathname.includes("/yt") ||
                    window.location.pathname.includes("claims"))) as boolean,
        );

        // Defer non-hero sections to allow the hero to paint first
        const scheduleRenderSections = () => {
            if (typeof window.requestIdleCallback === "function") {
                window.requestIdleCallback(() => setRenderAllSections(true), {
                    timeout: 1200,
                });
            } else {
                // Fallback for older browsers
                setTimeout(() => setRenderAllSections(true), 0);
            }
        };

        // When configured to render immediately, skip the deferral entirely.
        if (renderSectionsImmediately) {
            return;
        }

        // Give the browser a moment (next frame) before scheduling the rest
        const rafId = requestAnimationFrame(scheduleRenderSections);

        return () => {
            cancelAnimationFrame(rafId);
        };
    }, []);

    const stickySectionRef = useRef<HTMLDivElement>(null);
    const stickySectionWrapperRef = useRef<HTMLDivElement>(null);
    const nonStickySectionsRef = useRef<HTMLDivElement>(null);
    const [isDesktopViewport, setIsDesktopViewport] = useState(false);

    // Update desktop state so sticky layout is only used at min-width 1024px.
    useEffect(() => {
        const mediaQuery = window.matchMedia("(min-width: 1024px)");

        const updateViewportState = () => {
            setIsDesktopViewport(mediaQuery.matches);
        };

        updateViewportState();

        mediaQuery.addEventListener("change", updateViewportState);

        return () => {
            mediaQuery.removeEventListener("change", updateViewportState);
        };
    }, []);

    const sectionsForSticky = routeConfig?.config?.sections ?? [];
    const stickySectionOriginalIndexForSticky = sectionsForSticky.findIndex(
        (sec, indx) => sec.props?.isSticky && indx !== 0,
    );
    const stickySectionEnabledForSticky =
        stickySectionOriginalIndexForSticky !== -1;
    const useStickyLayoutForSticky =
        stickySectionEnabledForSticky && isDesktopViewport;

    useStickyElement(stickySectionRef, {
        getBoundary: () => nonStickySectionsRef.current,
        wrapperRef: stickySectionWrapperRef,
        enabled: useStickyLayoutForSticky,
        minWidth: 0,
    });

    if (!routeConfig || !routeConfig.config?.sections) {
        return <></>;
    }

    const storedReferrer =
        typeof window !== "undefined"
            ? sessionStorage.getItem("page_referrer")
            : null;

    const sections = routeConfig.config.sections;
    const heroSection = sections[0];
    const otherSections = sections.slice(1);

    // Sticky behavior:
    // - At most one section can be sticky.
    // - The hero section (index 0) is never allowed to be sticky, even when its isSticky prop is set to true.
    // - When present, on desktop viewport, the sticky section is rendered on the right side of the non-sticky sections and stays visible while scrolling within that area.
    //   On smaller viewports, the sticky section is rendered to be fixed to the bottom of the page
    const stickySectionOriginalIndex = sections.findIndex(
        (sec, indx) => sec.props?.isSticky && indx !== 0,
    );
    const stickySectionEnabled = stickySectionOriginalIndex !== -1;
    const useStickyDesktop = stickySectionEnabled && isDesktopViewport; // for desktop viewports only

    const stickySection = stickySectionEnabled
        ? sections[stickySectionOriginalIndex]
        : undefined;
    const nonStickySections = sections
        .map((sec, indx) => ({ section: sec, originalIndex: indx }))
        .filter(
            ({ section, originalIndex }) =>
                !section.props?.isSticky && originalIndex !== 0,
        );

    const legalNoticeText =
        visitDetails.stateCode === "CA"
            ? domain?.translations?.[
                  `${domainNameTranslation}_showLegalNoticeInsideCalifornia`
              ] ??
              "Advertising is paid for by attorneys participating in a joint advertising program, including Kevin Danesh, who are licensed to practice law only in California. A complete list of the attorneys participating in the program can be found ${sponsorsModal}. You may request an attorney by name. We are not a law firm or a lawyer referral service. This advertisement is not legal advice and does not guarantee or predict the outcome of your legal matter. Every case is different. Results depend on the laws, facts, and unique circumstances of each case. Hiring an attorney is an important decision that should not be based solely on advertising. Request free information about your attorney's background and experience. This advertisement does not imply greater quality in legal services than those provided by other attorneys. This advertisement does not imply that the attorneys are certified specialists or experts in any area of law. No legal services will be provided unless there is a signed agreement between the client and the attorney. We use cookies to personalize content and analyze website traffic. We also share information about your use of the site with our analytics partners, who may combine it with other data that you have provided or that they have collected through their services. By continuing to use this website, you agree to the use of cookies."
            : domain?.translations?.[
                  `${domainNameTranslation}_showLegalNoticeOutsideCalifornia`
              ] ??
              "Advertising announcements are paid for by attorneys participating in a joint advertising program who are licensed to practice law in their respective states. You can find a complete list of the attorneys participating in the joint program ${sponsorsModal}. You may request an attorney by name. We are not a law firm or a lawyer referral service. This advertisement is not legal advice and is not a guarantee or prediction of the outcome of your legal matter. Every case is different. Results depend on the laws, facts, and unique circumstances of each case. Hiring an attorney is an important decision that should not be based solely on advertising. Request free information about your attorney's background and experience. This advertisement does not imply greater quality in legal services than those provided by other attorneys. This advertisement does not imply that the attorneys are certified specialists or experts in any area of law. No legal services will be provided unless there is a signed agreement between the client and the attorney. We use cookies to personalize content and analyze website traffic. We also share information about your use of the site with our analytics partners, who may combine it with other data that you have provided or that they have collected through their services. By continuing to use this website, you agree to the use of cookies.";

    const processLegalNoticeText = (text: string) => {
        const placeholder = "${sponsorsModal}";
        const parts = text.split(placeholder);
        if (parts.length === 1) {
            return <>{text}</>;
        }
        return (
            <>
                {parts[0]}
                <span className="text-[blue] cursor-pointer">
                    {" "}
                    <span
                        id="modal-trigger"
                        className="color:#3498db; text-[#3498db]"
                        onClick={() => {
                            setShowPartnerPopup(true);
                        }}
                    >
                        {domain?.translations?.[
                            `${domainNameTranslation}_showLegalNoticeHereWord`
                        ] ?? "here"}
                    </span>
                </span>
                {parts[1]}
            </>
        );
    };

    const rulesVariablesMapping: Record<string, unknown> = {
        phoneNumber: phoneNumber,
        referral: referrer || storedReferrer,
        visit: visitDetails,
        visitor: userAgent,
    };

    const checkSectionRules = (
        mode: "show" | "hide",
        rules: SectionRules[][],
    ): boolean => {
        // No rules means don't hide the section
        if (!rules.length) return false;

        const isEmpty = (value: unknown): boolean =>
            value === undefined || value === null || value === "";

        const parseCommaSeparated = (value: string): string[] =>
            value.split(",").map((v) => v.trim());

        const evaluateRule = (ruleOption: SectionRules): boolean => {
            let ruleValue = rulesVariablesMapping[ruleOption.rule];

            // Extract nested property if codeName is specified
            if (
                ruleOption.codeName &&
                ruleValue &&
                typeof ruleValue === "object"
            ) {
                ruleValue = (ruleValue as Record<string, unknown>)[
                    ruleOption.codeName
                ];
            }

            const { operator, value } = ruleOption;

            switch (operator) {
                case "equal":
                    return ruleValue === value;

                case "notEqual":
                    return ruleValue !== value;

                case "notExist":
                    return isEmpty(ruleValue);

                case "exist":
                    return !isEmpty(ruleValue);

                case "includes":
                    if (typeof ruleValue === "string")
                        return ruleValue.includes(value);
                    if (Array.isArray(ruleValue))
                        return ruleValue.includes(value);
                    return false;

                case "notIncludes":
                    if (typeof ruleValue === "string")
                        return !ruleValue.includes(value);
                    if (Array.isArray(ruleValue))
                        return !ruleValue.includes(value);
                    return true; // If not string/array, treat as "not included"

                case "containsOne":
                    if (typeof ruleValue !== "string") return false;
                    return parseCommaSeparated(value).some((item) =>
                        (ruleValue as string).includes(item),
                    );

                case "containsAll":
                    if (typeof ruleValue !== "string") return false;
                    return parseCommaSeparated(value).every((item) =>
                        (ruleValue as string).includes(item),
                    );

                case "notOneOf":
                    if (typeof ruleValue !== "string") return true;
                    return parseCommaSeparated(value).every(
                        (item) => !(ruleValue as string).includes(item),
                    );

                default:
                    return false;
            }
        };

        // OR between groups, AND within each group
        const rulesMatch = rules.some((group) => group.every(evaluateRule));

        // "show" mode: hide section if rules DON'T match
        // "hide" mode: hide section if rules DO match
        return mode === "show" ? !rulesMatch : rulesMatch;
    };

    return (
        <>
            {/* Inject all CSS once - critical for LCP performance */}
            {allComponentCSS && (
                <Head>
                    <style
                        dangerouslySetInnerHTML={{ __html: allComponentCSS }}
                    />
                </Head>
            )}

            <div className="flex flex-col">
                {/* Hero section - always render immediately for LCP */}
                <Section
                    key={0}
                    variables={heroSection.variables ?? {}}
                    components={heroSection.components}
                    props={heroSection.props}
                    sectionIndex={0}
                    classIdentifier={heroSection.classIdentifier}
                />

                {/* check if sticky section exists and render it */}
                {stickySectionEnabled ? (
                    useStickyDesktop ? (
                        // ≥1024px – side-by-side: non-sticky left, sticky right
                        <div className="sticky-wrapper flex flex-row items-start gap-[30px]">
                            <div
                                ref={nonStickySectionsRef}
                                className="nonStickySections min-w-0 flex-1"
                            >
                                {nonStickySections &&
                                    nonStickySections.map(
                                        ({ section, originalIndex }) => {
                                            if (
                                                section.props &&
                                                section.props
                                                    ?.displaySectionRules &&
                                                checkSectionRules(
                                                    section.props
                                                        .displaySectionRules
                                                        .mode,
                                                    section.props
                                                        .displaySectionRules
                                                        .rules,
                                                )
                                            ) {
                                                return <></>;
                                            }

                                            return (
                                                <Section
                                                    key={originalIndex}
                                                    variables={
                                                        section.variables ?? {}
                                                    }
                                                    components={
                                                        section.components
                                                    }
                                                    props={section.props}
                                                    sectionIndex={originalIndex}
                                                    classIdentifier={`otherSection ${
                                                        section.classIdentifier ??
                                                        ""
                                                    }`}
                                                />
                                            );
                                        },
                                    )}
                            </div>

                            <div
                                ref={stickySectionWrapperRef}
                                className="stickySectionWrapper flex-none shrink-0 min-w-[350px]"
                            >
                                {stickySection && (
                                    <div ref={stickySectionRef}>
                                        <Section
                                            key={stickySectionOriginalIndex}
                                            variables={
                                                stickySection.variables ?? {}
                                            }
                                            components={
                                                stickySection.components
                                            }
                                            props={stickySection.props}
                                            sectionIndex={
                                                stickySectionOriginalIndex
                                            }
                                            classIdentifier={`stickySection ${
                                                stickySection.classIdentifier ??
                                                ""
                                            }`}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // <1024px – sticky section fixed to the bottom of the page, non-sticky sections scroll normally
                        <div
                            ref={nonStickySectionsRef}
                            className="relative w-full"
                        >
                            {stickySection && (
                                <div
                                    ref={stickySectionWrapperRef}
                                    className="fixed bottom-0 left-0 right-0 z-50 w-full"
                                    style={{
                                        height: "fit-content",
                                        maxHeight: "fit-content",
                                    }}
                                >
                                    <div ref={stickySectionRef}>
                                        <Section
                                            key={stickySectionOriginalIndex}
                                            variables={
                                                stickySection.variables ?? {}
                                            }
                                            components={
                                                stickySection.components
                                            }
                                            props={stickySection.props}
                                            sectionIndex={
                                                stickySectionOriginalIndex
                                            }
                                            classIdentifier={`stickySection ${
                                                stickySection.classIdentifier ??
                                                ""
                                            }`}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="w-full nonStickySections">
                                {renderAllSections &&
                                    nonStickySections.map(
                                        ({ section, originalIndex }) => {
                                            if (
                                                section.props &&
                                                section.props
                                                    ?.displaySectionRules &&
                                                checkSectionRules(
                                                    section.props
                                                        .displaySectionRules
                                                        .mode,
                                                    section.props
                                                        .displaySectionRules
                                                        .rules,
                                                )
                                            ) {
                                                return <></>;
                                            }

                                            return (
                                                <Section
                                                    key={originalIndex}
                                                    variables={
                                                        section.variables ?? {}
                                                    }
                                                    components={
                                                        section.components
                                                    }
                                                    props={section.props}
                                                    sectionIndex={originalIndex}
                                                    classIdentifier={`otherSection ${
                                                        section.classIdentifier ??
                                                        ""
                                                    }`}
                                                />
                                            );
                                        },
                                    )}
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        {/* Other sections - defer until after first paint */}
                        {/* hide section below form update is working w/ the the assumption that the hero section is always the one including the form, if this update is approved we'll adjust accordingly */}

                        {renderAllSections &&
                            otherSections.map((section, index: number) => {
                                if (
                                    section.props &&
                                    section.props?.displaySectionRules &&
                                    checkSectionRules(
                                        section.props.displaySectionRules.mode,
                                        section.props.displaySectionRules.rules,
                                    )
                                ) {
                                    return <></>;
                                }

                                return (
                                    <Section
                                        key={index + 1}
                                        variables={section.variables ?? {}}
                                        components={section.components}
                                        props={section.props}
                                        sectionIndex={index + 1}
                                        classIdentifier={`otherSection ${
                                            section.classIdentifier ?? ""
                                        }`}
                                    />
                                );
                            })}
                    </>
                )}
                {}

                {showLegalNotice && (
                    <section className="mb-[5rem]">
                        <div>
                            <p
                                id="legal-notice"
                                className="text-center text-xs text-gray-400"
                            >
                                {processLegalNoticeText(legalNoticeText)}
                            </p>
                        </div>
                        <Modal
                            onClose={() => {
                                setShowPartnerPopup(false);
                            }}
                            active={showModal}
                            closeIcon={<CloseIcon />}
                            backdropClosable
                            modalStyles={{
                                display: "flex",
                                flexDirection: "column",
                                maxHeight: "100%",
                            }}
                            modalClassName="h-[90vh] rounded"
                            bodyStyles={{ overflowY: "scroll" }}
                            width={900}
                            header={
                                domain?.translations?.[
                                    `${domainNameTranslation}_showLegalNoticeSponsorsWord`
                                ] ?? "Sponsors"
                            }
                        >
                            <div className="pb-5 lg:px-9 px-5">
                                <div className="flex flex-col">
                                    <div id="participants-container"></div>
                                </div>
                            </div>
                        </Modal>
                    </section>
                )}
            </div>
        </>
    );
}
